import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import axios from 'axios';
import config from '../../Config';
import { useDebounce } from 'use-debounce';
import CardPack from '../../Components/CardPack';
import { useHistory } from "react-router-dom";
import { useAtom } from 'jotai';
import ScrollToTopButton from '../../Components/ScrollToTopButton';
import DarkModeContext from '../../Components/DarkModeContext';
import { authUserAtom, isAuthAtom, dataUserAtom, packsListAtom, packsPageAtom, packsHasMoreAtom, packsSearchAtom, packsScrollYAtom } from '../../Services/store/atoms';
import AuthRequiredModal from '../../Components/AuthRequiredModal';
import LayoutHomeUser from '../../Layouts/Admin/Layout';
import Spinner from '../../Components/Spinner';
import { LuSearch, LuPackage } from 'react-icons/lu';
import { IoCloseCircle } from 'react-icons/io5';
import FluidMediaModal from '../../Components/FluidMediaModal2';

function ListPacksWithoutHeader(props) {
    const history = useHistory();
    const [authUser] = useAtom(authUserAtom);
    const [isAuth] = useAtom(isAuthAtom);
    const [dataUser] = useAtom(dataUserAtom);
    const { darkMode } = useContext(DarkModeContext);
    const [savedSearch, setSavedSearch] = useAtom(packsSearchAtom);
    const [searchValue, setSearchValue] = useState(savedSearch || '');
    const [value] = useDebounce(searchValue, 1000);
    const [isLoading, setIsLoading] = useState(false);
    const [products, setProducts] = useAtom(packsListAtom);
    const [showModal, setShowModal] = useState(false);
    useBodyScrollLock(showModal);
    const [nextPageUrl, setNextPageUrl] = useState(null);
    const [selectedPack, setSelectedPack] = useState(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [selectedMediaUrls, setSelectedMediaUrls] = useState([]);
    const [currentPage, setCurrentPage] = useAtom(packsPageAtom);
    const [hasMore, setHasMore] = useAtom(packsHasMoreAtom);
    const [scrollY, setScrollY] = useAtom(packsScrollYAtom);
    const fetched = useRef(false);

    const observer = useRef();
    const lastProductElementRef = useCallback(node => {
        if (isLoading || !hasMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                fetchProducts(currentPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoading, hasMore, currentPage]);

    const fetchProducts = useCallback(async (page = 1) => {
        if (isLoading) return;
        setIsLoading(true);
        
        try {
            const headers = {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                'Accept': 'application/json'
            };
            
            if (authUser?.token) {
                headers['Authorization'] = `Bearer ${authUser.token}`;
            }
            
            const requestData = new URLSearchParams();
            if (value && value.trim() !== '') {
                requestData.append('search', value.trim());
            }
            
            requestData.append('page', page);
            
            const response = await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/packs/list`, requestData, { headers });
            
            if (response.data && response.data.data) {
                setProducts(prevProducts => 
                    page === 1 ? response.data.data : [...prevProducts, ...response.data.data]
                );
                setHasMore(response.data.current_page < response.data.last_page);
                setCurrentPage(response.data.current_page);
                setNextPageUrl(response.data.next_page_url);
            }
        } catch (error) {
            console.error('Error al cargar los packs:', error);
        } finally {
            setIsLoading(false);
        }
    }, [authUser?.token, value, isLoading]);

    useEffect(() => {
        setSavedSearch(value);
    }, [value]);

    useEffect(() => {
        return () => setScrollY(window.scrollY);
    }, []);

    useEffect(() => {
        if (fetched.current) {
            setCurrentPage(1);
            setProducts([]);
            fetchProducts(1);
            return;
        }
        if (products.length === 0) {
            fetchProducts(1);
        } else if (scrollY > 0) {
            window.scrollTo(0, scrollY);
            setScrollY(0);
        }
        fetched.current = true;
    }, [value]);

    const handleSearch = (value) => {
        setSearchValue(value);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            setCurrentPage(1);
            setProducts([]);
            fetchProducts(1);
        }
    };

    const clickPack = (pack) => {
        if (isAuth) {
            setScrollY(window.pageYOffset);
            setSelectedPack(pack);
            setShowModal(true);
        } else {
            setShowLoginModal(true);
        }
    };

    const handleBuyPack = () => {
        setShowModal(false);
        setScrollY(window.scrollY);
        const buyRole = ['client', 'user'].includes(dataUser?.role) ? dataUser.role : 'client';
        history.push(`/${buyRole}/buyPack/${selectedPack.pack_id}`);
    };

    const handleCancelPurchase = () => {
        setShowModal(false);
        window.scrollTo(0, scrollY);
        setScrollY(0);
    };

    const handleCloseLoginModal = () => {
        setShowLoginModal(false);
    };

    const handleMediaClick = (mediaUrls) => {
        setSelectedMediaUrls(mediaUrls);
        setShowMediaModal(true);
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <LayoutHomeUser>
            <div className="ap-screen ap-screen-inset -m-2 lg:-m-4 p-2 lg:p-4 pb-10 min-h-[calc(100dvh-3.5rem)]">
            <div className="max-w-md mx-auto px-2 pt-4 pb-6">
                <label className="ap-search">
                    <LuSearch className="shrink-0" />
                    <input
                        type="search"
                        value={searchValue}
                        onChange={(e) => handleSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search packs..."
                        aria-label="Search packs"
                    />
                    {searchValue && (
                        <button type="button" onClick={() => handleSearch('')} aria-label="Clear search" className="shrink-0">
                            <IoCloseCircle className="text-lg" style={{ color: 'var(--ap-label-3)' }} />
                        </button>
                    )}
                </label>
            </div>

            {products.length > 0 ? (
                <div className='flex flex-wrap justify-center gap-4'>
                    {products.map((product, index) => (
                        <div 
                            key={product.pack_id} 
                            ref={index === products.length - 1 ? lastProductElementRef : null}
className="cursor-pointer"
                            >
                                <CardPack 
                                    pack={product}
                                    onClick={clickPack}
                                    onMediaClick={handleMediaClick}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                !isLoading && (
                    <div className="flex flex-col items-center mt-16">
                        <LuPackage className="text-5xl" style={{ color: 'var(--ap-label-3)' }} />
                        <p className="ap-subhead mt-4">No hay packs para mostrar</p>
                    </div>
                )
            )}

            {isLoading && <Spinner />}
            <ScrollToTopButton onClick={scrollToTop} />
            </div>

            {showModal && (
                <div className="fixed z-confirm inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-dvh pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-black/40 transition-opacity" aria-hidden="true"></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-dvh" aria-hidden="true">&#8203;</span>

                        <div className="ap-card inline-block align-bottom text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full" style={{ boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
                            <div className="p-5 flex flex-col">
                                <div className={'flex flex-row justify-between items-center'}>
                                    <img src={selectedPack?.media?.length > 0 ? selectedPack.media[0].url : 'https://via.placeholder.com/150'} alt={selectedPack?.name} className="w-24 h-24 object-cover rounded-[14px]" />
                                    <div className="flex flex-col ml-4">
                                        <h3 className="ap-headline" id="modal-title">
                                            {selectedPack?.name}
                                        </h3>
                                        <p className="ap-footnote mt-1">
                                            {selectedPack?.description}
                                        </p>
                                        <p className="ap-subhead mt-1">
                                            {selectedPack?.price} coins
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="px-5 pb-5 flex flex-col-reverse sm:flex-row gap-3">
                                <button
                                    type="button"
                                    className="ap-btn ap-btn-gray w-full sm:flex-1"
                                    onClick={handleCancelPurchase}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="ap-btn ap-btn-filled w-full sm:flex-1"
                                    onClick={handleBuyPack}
                                >
                                    Comprar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showLoginModal && (
                <AuthRequiredModal 
                    show={showLoginModal} 
                    onClose={handleCloseLoginModal} 
                    message="Debes iniciar sesión para comprar este pack"
                    darkMode={darkMode}
                />
            )}

            {showMediaModal && (
                <FluidMediaModal
                    show={showMediaModal}
                    onClose={() => setShowMediaModal(false)}
                    mediaUrls={selectedMediaUrls}
                />
            )}
        </LayoutHomeUser>
    );
}

export default ListPacksWithoutHeader;